import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

//Utils
import { setFocusOnFormSubmit } from '../../utils/helpers';

// Components
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import { useAppSelector } from 'hooks';
import { selectIsAuthorIconVisible } from 'reducers/workflows';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { Box } from '@mui/material';
import { IrArticleTextMessage } from 'components/IrArticleTextMessage/IrArticleTextMessage';

interface FormWrapperProps {
  /** Element to display inside the form */
  children: React.ReactNode;
  className?: string;
  /** Boolean to determine if submit on enter should be disabled */
  disableSubmitOnEnter?: boolean;
  /** Title message for the form */
  formTitle?: string | null;
  /** Boolean to determine if submit button is disabled */
  isDisabled: boolean;
  isLastStep: boolean;
  /** Function that handles onSubmit event */
  onSubmit: (e: React.SyntheticEvent) => void;
  shouldRenderTitleAsMarkdown?: boolean;
  stepIndex: number;
  /** Submit button text */
  submitButtonText: string;
}

const FormWrapper: React.FC<React.PropsWithChildren<FormWrapperProps>> = ({
  children,
  className,
  disableSubmitOnEnter,
  formTitle,
  isDisabled,
  onSubmit,
  shouldRenderTitleAsMarkdown = false,
  stepIndex,
  submitButtonText,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  useEffect(() => {
    return () => setFocusOnFormSubmit(formRef);
  }, []);

  return (
    // eslint-disable-next-line styled-components-a11y/no-noninteractive-element-interactions
    <FormContainer
      className={className}
      data-testid='formWrapper'
      onKeyPress={e => {
        if (e.key === 'Enter' && !isDisabled && !disableSubmitOnEnter)
          onSubmit(e);
      }}
      onSubmit={(e: React.FormEvent) => e.preventDefault()}
      ref={formRef}
    >
      {formTitle && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {shouldRenderTitleAsMarkdown ? (
            <IrArticleTextMessage message={formTitle} />
          ) : (
            formTitle
          )}
        </AgathaMessage>
      )}
      <Box sx={{ paddingX: '8px' }}>
        <ContentContainer>
          {children}
          <OrgThemedButton
            data-testid='formWrapper-submit-button'
            disabled={isDisabled}
            fullWidth
            onClick={e => {
              if (!isDisabled) onSubmit(e);
            }}
            variant='contained'
          >
            {submitButtonText}
          </OrgThemedButton>
        </ContentContainer>
      </Box>
    </FormContainer>
  );
};

export default FormWrapper;

const FormContainer = styled('form')`
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  margin-bottom: 16px;
`;

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  line-height: 22px;
`;
